import { ImmomioButtonComponent } from '@immomio/ui/button';
import {
  Component,
  inject,
  Input,
  OnInit,
  input,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Constants, SelfDisclosureQuestion } from '@ui/shared/model';

import { TranslatePipe } from '@ngx-translate/core';

import { SelfDisclosureService } from '../../self-disclosure.service';
import { SelfDisclosurePersonComponent } from '../self-disclosure-person/self-disclosure-person.component';

import { getArrayListValidator } from '../../../components/legacy/form/controls/validation/validators';

@Component({
  selector: 'app-self-disclosure-person-list',
  templateUrl: './self-disclosure-person-list.component.html',
  styleUrls: ['./self-disclosure-person-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ImmomioButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    SelfDisclosurePersonComponent,
    TranslatePipe
  ]
})
export class SelfDisclosurePersonListComponent implements OnInit {
  #destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private selfDisclosureService = inject(SelfDisclosureService);

  private _selfDisclosureQuestion: SelfDisclosureQuestion;
  @Input()
  get selfDisclosureQuestion() {
    return this._selfDisclosureQuestion;
  }

  set selfDisclosureQuestion(value) {
    this._selfDisclosureQuestion = { ...value };
  }

  readonly form = input<FormGroup>(undefined);
  readonly confirmations = input<string[]>(undefined);
  readonly constants = input<Constants>(undefined);

  public get getFormControls() {
    return (this.getFormArray.controls as FormGroup[]) || [];
  }

  private get getAnswerUnavailable() {
    return this.form().get('answerUnavailable');
  }

  private get getFormArray() {
    return this.form().get('answer') as FormArray;
  }

  public ngOnInit() {
    this.getAnswerUnavailable.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(value => {
        this.selfDisclosureQuestion.answerUnavailable =
          this.getAnswerUnavailable.value;
        this.updateValidity(value);
      });
    this.updateValidity(this.selfDisclosureQuestion.answerUnavailable);
  }

  public removePerson(index: number) {
    this.getFormArray.removeAt(index);
  }

  public addPerson() {
    const personConfig = this.selfDisclosureService.getPersonConfig(
      this.selfDisclosureQuestion.mandatory &&
        this.selfDisclosureQuestion.showSelfDisclosureQuestions
    );

    personConfig.subQuestions = this.fb.array([]);
    personConfig.confirmations = this.fb.array([]);

    this.selfDisclosureQuestion.subQuestions.forEach(subQuestion => {
      const config = this.selfDisclosureService.getConfigForSubQuestion(
        subQuestion.type,
        subQuestion.mandatory && subQuestion.showSelfDisclosureQuestions
      );
      const formGroup: FormGroup = this.fb.group(config);
      formGroup.patchValue(subQuestion); // need to patch, so all fields are saved in answer
      (personConfig.subQuestions as FormArray).push(formGroup);
    });
    this.confirmations().forEach(text => {
      const confirmationGroup = this.fb.group(
        this.selfDisclosureService.getConfirmationConfig(text)
      );
      (personConfig.confirmations as FormArray).push(confirmationGroup);
    });
    this.getFormArray.push(this.fb.group(personConfig));
  }

  private updateValidity(value: boolean) {
    /**
     * Here we add or remove the validators to show the red border only if field is invalid
     */
    if (value) {
      this.getFormArray.clearValidators();
    } else {
      this.getFormArray.setValidators(
        getArrayListValidator(
          this.selfDisclosureQuestion.mandatory &&
            this.selfDisclosureQuestion.showSelfDisclosureQuestions
        )
      );
    }
    this.getFormArray.updateValueAndValidity();
  }
}
